import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * A custom element that creates a pinned, scroll-driven text illumination effect
 * matching the reference video across both mobile and desktop.
 *
 * In Atelier's desktop layout (≥990px), `.page-wrapper` is the scroll container
 * rather than `window`. This component listens to the active scroll target, captures
 * document scroll events, and synchronizes the sticky pinned viewport animation.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [target] - The heading element containing the narrative text.
 * @property {HTMLElement} [button] - The call to action button.
 * @property {HTMLElement} [viewport] - The sticky viewport container.
 *
 * @extends Component<Refs>
 */
class ReadingTextComponent extends Component {
  requiredRefs = ['target'];

  /** @type {HTMLSpanElement[]} */
  #chars = [];

  /** @type {number} */
  #initialOpacity = 0.15;

  /** @type {number | null} */
  #rafId = null;

  /** @type {Set<EventTarget>} */
  #activeScrollTargets = new Set();

  connectedCallback() {
    super.connectedCallback();

    const target = this.refs.target;
    if (!target) return;

    this.#initialOpacity = parseFloat(this.getAttribute('data-initial-opacity') || '0.15');

    this.#splitText(target);

    if (prefersReducedMotion()) {
      for (let i = 0; i < this.#chars.length; i++) {
        this.#chars[i].style.opacity = '1';
      }
      if (this.refs.button) {
        this.refs.button.style.opacity = '1';
      }
      return;
    }

    this.#bindScrollListeners();

    // Initial calculation on load
    this.#updateProgress();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindScrollListeners();
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  /**
   * Splits text content into words and characters while preserving screen-reader accessibility.
   *
   * @param {HTMLElement} target
   */
  #splitText(target) {
    const rawText = target.textContent?.trim() || '';
    if (!rawText) return;

    // Accessibility: Ensure screen readers announce the full text continuously
    target.setAttribute('aria-label', rawText);

    const words = rawText.split(/\s+/);
    target.innerHTML = '';
    this.#chars = [];

    const wrapper = document.createElement('span');
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.className = 'reading-text__split-wrapper';

    for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
      const word = words[wordIndex];
      const wordSpan = document.createElement('span');
      wordSpan.className = 'reading-text__word';

      // Split word into individual characters
      for (const char of word) {
        const charSpan = document.createElement('span');
        charSpan.className = 'reading-text__char';
        charSpan.textContent = char;
        charSpan.style.opacity = `${this.#initialOpacity}`;
        wordSpan.appendChild(charSpan);
        this.#chars.push(charSpan);
      }

      wrapper.appendChild(wordSpan);

      // Add space between words
      if (wordIndex < words.length - 1) {
        const spaceSpan = document.createElement('span');
        spaceSpan.className = 'reading-text__space';
        spaceSpan.innerHTML = '&nbsp;';
        wrapper.appendChild(spaceSpan);
      }
    }

    target.appendChild(wrapper);

    // Initial button opacity matches initial unrevealed state
    if (this.refs.button) {
      this.refs.button.style.opacity = `${this.#initialOpacity}`;
    }
  }

  /**
   * Binds scroll listeners to all potential scroll containers (handles desktop .page-wrapper,
   * mobile document root, and capture-phase scroll events).
   */
  #bindScrollListeners() {
    this.#unbindScrollListeners();

    // 1. Primary scroll target from theme (@theme/scroll-container handles desktop .page-wrapper)
    try {
      const primaryTarget = getScrollEventTarget();
      if (primaryTarget) {
        primaryTarget.addEventListener('scroll', this.#onScroll, { passive: true });
        this.#activeScrollTargets.add(primaryTarget);
      }
    } catch (_) {
      // Fallback if helper is not yet available
    }

    // 2. Query .page-wrapper directly for desktop squeeze layout
    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper && !this.#activeScrollTargets.has(pageWrapper)) {
      pageWrapper.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#activeScrollTargets.add(pageWrapper);
    }

    // 3. Document in capture phase (captures non-bubbling scroll events from ANY scrolling container)
    document.addEventListener('scroll', this.#onScroll, { passive: true, capture: true });
    this.#activeScrollTargets.add(document);

    // 4. Window for standard document root scroll
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#activeScrollTargets.add(window);

    // 5. Watch for responsive layout breakpoint shifts (mobile <-> desktop)
    window.addEventListener('resize', this.#onResize, { passive: true });
    if (scrollContainerMediaQuery) {
      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  #unbindScrollListeners() {
    for (const target of this.#activeScrollTargets) {
      target.removeEventListener('scroll', this.#onScroll);
      target.removeEventListener('scroll', this.#onScroll, { capture: true });
    }
    this.#activeScrollTargets.clear();

    window.removeEventListener('resize', this.#onResize);
    if (scrollContainerMediaQuery) {
      scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    }
  }

  #handleBreakpointChange = () => {
    this.#bindScrollListeners();
    this.#updateProgress();
  };

  #onResize = () => {
    this.#onScroll();
  };

  #onScroll = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#updateProgress();
    });
  };

  #updateProgress() {
    if (this.#chars.length === 0) return;

    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight;

    // Viewport height of the sticky area
    const viewport = this.refs.viewport;
    const viewportHeight = viewport ? viewport.offsetHeight : vh;

    // Component total scroll track height
    const totalHeight = this.offsetHeight;
    const scrollDistance = Math.max(100, totalHeight - viewportHeight);

    // Measure effective top sticky header offset if present
    const headerOffset = this.#getHeaderOffset();

    // How far the component has scrolled past the sticky top threshold
    const scrolled = headerOffset - rect.top;
    let progress = scrolled / scrollDistance;
    progress = Math.max(0, Math.min(1, progress));

    // The text reveal spans the first 85% of the scroll track
    const textProgress = Math.min(1, progress / 0.85);

    const totalChars = this.#chars.length;
    const activeProgress = textProgress * totalChars;
    const fadeRange = 2.0; // Smooth 2-character gradient highlight

    for (let i = 0; i < totalChars; i++) {
      const char = this.#chars[i];
      if (i <= activeProgress - fadeRange) {
        char.style.opacity = '1';
      } else if (i >= activeProgress + fadeRange) {
        char.style.opacity = `${this.#initialOpacity}`;
      } else {
        const charProgress = (activeProgress - (i - fadeRange)) / (2 * fadeRange);
        const clamped = Math.max(0, Math.min(1, charProgress));
        const op = this.#initialOpacity + (1 - this.#initialOpacity) * clamped;
        char.style.opacity = op.toFixed(3);
      }
    }

    // Button reveal starts at 72% scroll progress and reaches 100% full opacity at progress = 1.0
    if (this.refs.button) {
      if (progress < 0.72) {
        this.refs.button.style.opacity = `${this.#initialOpacity}`;
      } else {
        const btnProgress = (progress - 0.72) / 0.28;
        const clamped = Math.max(0, Math.min(1, btnProgress));
        const btnOp = this.#initialOpacity + (1 - this.#initialOpacity) * clamped;
        this.refs.button.style.opacity = btnOp.toFixed(3);
      }
    }
  }

  /**
   * Calculates the visible sticky header height that offsets the top of the viewport.
   *
   * @returns {number}
   */
  #getHeaderOffset() {
    const rawVal = getComputedStyle(document.body).getPropertyValue('--header-height');
    const parsed = parseFloat(rawVal);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }

    const header = document.querySelector('#header-component') || document.querySelector('.header-section');
    if (header) {
      const headerRect = header.getBoundingClientRect();
      if (headerRect.top <= 2 && headerRect.bottom > 0) {
        return headerRect.height;
      }
    }

    return 0;
  }
}

if (!customElements.get('reading-text-component')) {
  customElements.define('reading-text-component', ReadingTextComponent);
}

export { ReadingTextComponent };
